import { Observable, throwError, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AxiosResponse } from 'axios';
import { Axios } from './axios';
import { RoomRepository } from '../feature/room';
import { SessionRepository } from '../feature/session';

export enum ErrorDataCode {
  InvalidSessionId = 'InvalidSessionId',
  NotAcceptable = 'INVALID_MODEL_STATE',
}

export interface OnErrorData {
  error: boolean;
  message: string;
  status: number;
  body?: { Message: string; Code: ErrorDataCode; Errors?: string[] };
}

export interface FetchOptions {
  preventShare: boolean;
}

const onAxiosSuccessFetch = <T, D = any>(
  response: AxiosResponse<T, D>
): Observable<T> => {
  if (response.status === 204) {
    return of({} as T);
  }
  if (response.status !== 200) {
    return error(
      getErrorMessage(response.statusText, response.status, response.data)
    );
  }
  return of(response.data);
};

const onErrorFetch = ({ response }: any) => {
  return error(
    getErrorMessage(response.message, response.status, response.data)
  );
};

const error = (err: any) => throwError(err);

const isInvalidSessionResponse = (err: OnErrorData) => {
  return (
    err.status === 400 &&
    err.body &&
    err.body.Code === ErrorDataCode.InvalidSessionId
  );
};

const isNotAcceptableResponse = (err: OnErrorData) => {
  return (
    err.status === 406 &&
    err.body &&
    err.body.Code === ErrorDataCode.NotAcceptable
  );
};

const getErrorMessage = (
  message: string,
  status: number,
  body?: any
): OnErrorData => {
  return {
    error: true,
    message,
    status,
    body: { Code: body.Code, Message: body.Message },
  };
};

const getHttpHeaders = (headers: RequestInit['headers']) => {
  // todo dinamicizzare quando funzionerà
  const baseHeaders = {
    'Content-type': 'application/json; charset=UTF-8',
  };
  return headers ? { ...baseHeaders, ...headers } : baseHeaders;
};

const get = <T>(url: string, options: RequestInit = {}): Observable<T> => {
  return Axios.get<T>(url, { headers: getHttpHeaders(options.headers) }).pipe(
    switchMap(onAxiosSuccessFetch),
    catchError(onErrorFetch)
  );
};

const post = <T, S>(
  url: string,
  body: S,
  options: RequestInit = {}
): Observable<T> => {
  RoomRepository.store.reset();
  SessionRepository.store.reset();
  return Axios.post<T>(
    url,
    {
      ...body,
    },
    { headers: getHttpHeaders(options.headers) }
  ).pipe(switchMap(onAxiosSuccessFetch), catchError(onErrorFetch));
};

const patch = <T, S>(
  url: string,
  body: S,
  options: RequestInit = {}
): Observable<T> => {
  RoomRepository.store.reset();
  SessionRepository.store.reset();
  return Axios.patch<T>(
    url,
    {
      ...body,
    },
    { headers: getHttpHeaders(options.headers) }
  ).pipe(switchMap(onAxiosSuccessFetch), catchError(onErrorFetch));
};

const httpDelete = <T>(
  url: string,
  options: RequestInit = {}
): Observable<T> => {
  RoomRepository.store.reset();
  SessionRepository.store.reset();
  return Axios.delete<T>(url, {
    headers: getHttpHeaders(options.headers),
  }).pipe(switchMap(onAxiosSuccessFetch), catchError(onErrorFetch));
};

export const Http = {
  get,
  post,
  delete: httpDelete,
  patch,
  error,
  isInvalidSessionResponse,
  isNotAcceptableResponse,
};
